import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import ParallaxBackground from './ParallaxBackground';
import { contactParallaxConfig } from './ParallaxConfigs';
import { colors } from '../styles/colors';
import FloatingElements from './FloatingElements';

const ContactSection = styled.section`
  padding: 100px 0;
  background: ${colors.background.secondary};
  position: relative;
  overflow: hidden;
`;

const FloatingElement = styled(motion.div)`
  position: absolute;
  width: 100px;
  height: 100px;
  background-size: contain;
  background-repeat: no-repeat;
  opacity: 0.1;
  z-index: 0;
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  font-family: "BRLNSDB";
  color: ${colors.text.primary};
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  display: inline-block;

  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 4px;
    background: ${colors.gradients.primary};
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4rem;
`;

const ContactForm = styled(motion.form)`
  background: ${colors.background.primary};
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px ${colors.shadow.primary};
  width: 100%;
  font-family: "Montserrat";
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormGroupFull = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  font-family: "BRLNSDB";
  display: block;
  margin-bottom: 0.5rem;
  color: ${colors.text.primary};
  font-weight: 500;
  text-align: left;
  font-family: "";
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.shadow.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.shadow.primary};
  }
`;

const SubmitButton = styled(motion.button)`
  background: ${colors.gradients.primary};
  color: ${colors.text.light};
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px ${colors.shadow.primary};
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Message = styled(motion.div)`
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
  background: ${props => props.success ? '#C6F6D5' : '#FED7D7'};
  color: ${props => props.success ? '#2F855A' : '#C53030'};
`;

const ContactInfo = styled(motion.div)`
  background: ${colors.background.primary};
  padding: 3rem;
  border-radius: 20px;
  box-shadow: 0 10px 30px ${colors.shadow.primary};
  width: 100%;
`;

const InfoTitle = styled.h3`
  font-size: 1.8rem;
  color: ${colors.text.primary};
  margin-bottom: 2rem;
  position: relative;
  padding-bottom: 1rem;
  font-family: "BRLNSDB";
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background: ${colors.gradients.primary};
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 2rem;
  font-family: "Montserrat";
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 1.5rem;
  background: ${colors.background.secondary};
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px ${colors.shadow.primary};
  }
`;

const InfoIcon = styled.span`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: ${colors.gradients.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.light};
  font-size: 2rem;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px ${colors.shadow.primary};
`;

const InfoText = styled.p`
  font-size: 1.1rem;
  color: ${colors.text.secondary};
  line-height: 1.6;
`;

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [status, setStatus] = useState({ type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatus({ type: '', message: '' });

        try {
            const response = await fetch('https://ped.leeringenieria.com/pedliteBack/public/api/enviarCorreoSoporte', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                throw new Error('Error al enviar el mensaje');
            }

            setStatus({
                type: 'success',
                message: '¡Mensaje enviado con éxito! Nos pondremos en contacto contigo pronto.'
            });

            setFormData({ name: '', email: '', message: '' });
        } catch (error) {
            setStatus({
                type: 'error',
                message: 'Hubo un error al enviar el mensaje. Por favor, intenta nuevamente.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <ContactSection id="contact">
            <FloatingElements section="contact" />
            <ContactContainer>
                <Title
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    Contáctanos
                </Title>

                <ContentWrapper>
                    <ContactForm
                        onSubmit={handleSubmit}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        {status.message && (
                            <Message
                                success={status.type === 'success'}
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                {status.message}
                            </Message>
                        )}

                        <FormRow>
                            <FormGroup>
                                <Label htmlFor="name">Nombre</Label>
                                <Input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label htmlFor="email">Email</Label>
                                <Input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </FormGroup>
                        </FormRow>

                        <FormGroupFull>
                            <Label htmlFor="message">Mensaje</Label>
                            <TextArea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                            />
                        </FormGroupFull>

                        <SubmitButton
                            type="submit"
                            disabled={isSubmitting}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                        </SubmitButton>
                    </ContactForm>

                    <ContactInfo
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        <InfoTitle>Información de Contacto</InfoTitle>

                        <InfoGrid>
                            <InfoItem>
                                <InfoIcon>📍</InfoIcon>
                                <InfoText>
                                    Carrera 19b1 # 9c - 92<br />
                                    Barrio los cortijos, Valledupar - Cesar, Colombia
                                </InfoText>
                            </InfoItem>

                            <InfoItem>
                                <InfoIcon>📞</InfoIcon>
                                <InfoText>+57 316 491 5332</InfoText>
                            </InfoItem>

                            <InfoItem>
                                <InfoIcon>✉️</InfoIcon>
                                <InfoText>soporte.pedigital@gmail.com</InfoText>
                            </InfoItem>
                        </InfoGrid>
                    </ContactInfo>
                </ContentWrapper>
            </ContactContainer>
        </ContactSection>
    );
};

export default Contact; 